import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError, money } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";

const GRADES = [
  "Kindergarten", "1st Grade", "2nd Grade", "3rd Grade", "4th Grade", "5th Grade",
  "6th Grade", "7th Grade", "8th Grade", "9th Grade", "10th Grade", "11th Grade", "12th Grade",
];

const emptyStudent = () => ({
  legal_name: "",
  date_of_birth: "",
  grade_level: "",
  academic_year: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
  state_of_residence: "",
});

const STEP_LABELS = ["Parent / Guardian", "Students", "Acknowledgment", "Payment"];

export default function PartnerEnroll() {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [parent, setParent] = useState({
    parent_name: "", email: "", phone: "", address: "", city: "", state: "",
    zip_code: "", country: "United States", enrollment_type: "initial",
  });
  const [students, setStudents] = useState([emptyStudent()]);
  const [ack, setAck] = useState(false);

  useEffect(() => {
    api
      .get(`/public/partners/${slug}`)
      .then(({ data }) => setPartner(data))
      .catch(() => setNotFound(true));
    api.get("/public/settings").then(({ data }) => setPricing(data)).catch(() => {});
  }, [slug]);

  const total = pricing
    ? (parent.enrollment_type === "initial"
        ? pricing.price_initial_enrollment
        : pricing.price_annual_reenrollment) +
      pricing.price_records_review * students.length
    : 0;

  const setStudent = (i, key, value) =>
    setStudents((prev) => prev.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)));

  const validate = () => {
    if (step === 0) {
      const required = ["parent_name", "email", "phone", "address", "city", "state", "zip_code", "country"];
      if (required.some((k) => !String(parent[k] || "").trim())) {
        toast.error("Please complete all parent/guardian fields");
        return false;
      }
    }
    if (step === 1) {
      const bad = students.some(
        (s) => !s.legal_name.trim() || !s.date_of_birth || !s.grade_level || !s.academic_year || !s.state_of_residence.trim()
      );
      if (bad) {
        toast.error("Please complete every student's information");
        return false;
      }
    }
    if (step === 2 && !ack) {
      toast.error("You must accept the acknowledgment to continue");
      return false;
    }
    return true;
  };

  const submitAndPay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/enroll", {
        partner_slug: slug,
        ...parent,
        acknowledgment_accepted: true,
        students,
      });
      const checkout = await api.post("/payments/checkout", {
        family_id: data.family_id,
        origin_url: window.location.origin,
      });
      trackEvent("Started enrollment payment (reached Stripe checkout)");
      window.location.href = checkout.data.checkout_url;
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  if (notFound)
    return (
      <div className="flex min-h-screen items-center justify-center px-6 text-left">
        <div className="max-w-md">
          <h1 className="font-display text-2xl font-bold text-slate-900">Partner page not found</h1>
          <p className="mt-3 text-sm text-slate-600">
            The partner path <span className="font-mono">/{slug}</span> is not active. Please check
            the link provided by your educational partner.
          </p>
          <Link to="/" className="mt-6 inline-block">
            <Button variant="outline">Return home</Button>
          </Link>
        </div>
      </div>
    );

  if (!partner || !pricing)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );

  return (
    <div className="min-h-screen bg-slate-50 text-left">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Link to="/">
            <Brand />
          </Link>
          <span
            className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700"
            data-testid="partner-name-badge"
          >
            {partner.name}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10">
        <h1 className="font-display text-3xl font-bold text-slate-900 sm:text-4xl">
          Family Enrollment
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          You are enrolling with Alabama Church School through{" "}
          <span className="font-semibold text-slate-900">{partner.name}</span>.
        </p>

        <ol className="mt-8 flex flex-wrap gap-2" data-testid="enroll-steps">
          {STEP_LABELS.map((label, i) => (
            <li
              key={label}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                i === step
                  ? "border-slate-900 bg-slate-900 text-white"
                  : i < step
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 bg-white text-slate-500"
              }`}
            >
              {i + 1}. {label}
            </li>
          ))}
        </ol>

        <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="mt-6 border-slate-200">
            <CardContent className="p-6 sm:p-8">
              {step === 0 && (
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Parent / guardian legal name" className="sm:col-span-2">
                    <Input
                      value={parent.parent_name}
                      onChange={(e) => setParent({ ...parent, parent_name: e.target.value })}
                      data-testid="parent-name-input"
                    />
                  </Field>
                  <Field label="Email">
                    <Input
                      type="email"
                      value={parent.email}
                      onChange={(e) => setParent({ ...parent, email: e.target.value })}
                      data-testid="parent-email-input"
                    />
                  </Field>
                  <Field label="Phone">
                    <Input
                      value={parent.phone}
                      onChange={(e) => setParent({ ...parent, phone: e.target.value })}
                      data-testid="parent-phone-input"
                    />
                  </Field>
                  <Field label="Mailing address" className="sm:col-span-2">
                    <Input
                      value={parent.address}
                      onChange={(e) => setParent({ ...parent, address: e.target.value })}
                      data-testid="parent-address-input"
                    />
                  </Field>
                  <Field label="City">
                    <Input
                      value={parent.city}
                      onChange={(e) => setParent({ ...parent, city: e.target.value })}
                      data-testid="parent-city-input"
                    />
                  </Field>
                  <Field label="State">
                    <Input
                      value={parent.state}
                      onChange={(e) => setParent({ ...parent, state: e.target.value })}
                      data-testid="parent-state-input"
                    />
                  </Field>
                  <Field label="ZIP / postal code">
                    <Input
                      value={parent.zip_code}
                      onChange={(e) => setParent({ ...parent, zip_code: e.target.value })}
                      data-testid="parent-zip-input"
                    />
                  </Field>
                  <Field label="Country">
                    <Input
                      value={parent.country}
                      onChange={(e) => setParent({ ...parent, country: e.target.value })}
                      data-testid="parent-country-input"
                    />
                  </Field>
                  <Field label="Enrollment type" className="sm:col-span-2">
                    <Select
                      value={parent.enrollment_type}
                      onValueChange={(v) => setParent({ ...parent, enrollment_type: v })}
                    >
                      <SelectTrigger data-testid="enrollment-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="initial">
                          Initial enrollment ({money(pricing.price_initial_enrollment)})
                        </SelectItem>
                        <SelectItem value="reenrollment">
                          Annual re-enrollment ({money(pricing.price_annual_reenrollment)})
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-8">
                  {students.map((s, i) => (
                    <div key={i} className="border-b border-slate-200 pb-6 last:border-0 last:pb-0">
                      <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-base font-semibold text-slate-900">Student {i + 1}</h2>
                        {students.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 text-red-600"
                            onClick={() => setStudents(students.filter((_, idx) => idx !== i))}
                            data-testid={`remove-student-${i}`}
                          >
                            <Trash2 className="h-4 w-4" /> Remove
                          </Button>
                        )}
                      </div>
                      <div className="grid gap-5 sm:grid-cols-2">
                        <Field label="Student legal name" className="sm:col-span-2">
                          <Input
                            value={s.legal_name}
                            onChange={(e) => setStudent(i, "legal_name", e.target.value)}
                            data-testid={`student-name-input-${i}`}
                          />
                        </Field>
                        <Field label="Date of birth">
                          <Input
                            type="date"
                            value={s.date_of_birth}
                            onChange={(e) => setStudent(i, "date_of_birth", e.target.value)}
                            data-testid={`student-dob-input-${i}`}
                          />
                        </Field>
                        <Field label="Grade level">
                          <Select
                            value={s.grade_level}
                            onValueChange={(v) => setStudent(i, "grade_level", v)}
                          >
                            <SelectTrigger data-testid={`student-grade-select-${i}`}>
                              <SelectValue placeholder="Select grade" />
                            </SelectTrigger>
                            <SelectContent>
                              {GRADES.map((g) => (
                                <SelectItem key={g} value={g}>
                                  {g}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label="Academic year">
                          <Input
                            value={s.academic_year}
                            onChange={(e) => setStudent(i, "academic_year", e.target.value)}
                            data-testid={`student-year-input-${i}`}
                          />
                        </Field>
                        <Field label="State of residence">
                          <Input
                            value={s.state_of_residence}
                            onChange={(e) => setStudent(i, "state_of_residence", e.target.value)}
                            data-testid={`student-state-input-${i}`}
                          />
                        </Field>
                      </div>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => setStudents([...students, emptyStudent()])}
                    data-testid="add-student-button"
                  >
                    <Plus className="h-4 w-4" /> Add another child
                  </Button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <h2 className="text-base font-semibold text-slate-900">
                    Out-of-state acknowledgment
                  </h2>
                  <p className="rounded-md border border-slate-200 bg-slate-50 p-5 text-sm leading-relaxed text-slate-700">
                    {pricing.acknowledgment_text}
                  </p>
                  <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-800">
                    <Checkbox
                      checked={ack}
                      onCheckedChange={(v) => setAck(Boolean(v))}
                      data-testid="acknowledgment-checkbox"
                    />
                    <span>
                      I am the parent or legal guardian and I accept this acknowledgment.
                    </span>
                  </label>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6">
                  <h2 className="text-base font-semibold text-slate-900">Review &amp; payment</h2>
                  <div className="space-y-3 rounded-md border border-slate-200 p-5 text-sm">
                    <Row label="Parent / guardian" value={parent.parent_name} />
                    <Row label="Email" value={parent.email} />
                    <Row label="Educational partner" value={partner.name} />
                    <Row label="Students" value={String(students.length)} />
                    <Row
                      label={
                        parent.enrollment_type === "initial"
                          ? "Initial family enrollment"
                          : "Annual family re-enrollment"
                      }
                      value={money(
                        parent.enrollment_type === "initial"
                          ? pricing.price_initial_enrollment
                          : pricing.price_annual_reenrollment
                      )}
                    />
                    <Row
                      label={`Annual records review × ${students.length}`}
                      value={money(pricing.price_records_review * students.length)}
                    />
                    <div className="flex items-center justify-between border-t border-slate-200 pt-3 text-base font-semibold text-slate-900">
                      <span>Total due</span>
                      <span data-testid="enroll-total">{money(total)}</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    You will be redirected to Stripe to pay Alabama Church School securely. Applicable
                    tax is calculated at checkout.
                  </p>
                  <Button
                    className="w-full gap-2"
                    size="lg"
                    disabled={busy}
                    onClick={submitAndPay}
                    data-testid="enroll-pay-button"
                  >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />} Complete enrollment &amp; pay
                  </Button>
                </div>
              )}

              <div className="mt-8 flex items-center justify-between">
                <Button
                  variant="ghost"
                  className="gap-2"
                  disabled={step === 0}
                  onClick={() => setStep(step - 1)}
                  data-testid="enroll-back-button"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
                {step < 3 && (
                  <Button
                    className="gap-2"
                    onClick={() => validate() && setStep(step + 1)}
                    data-testid="enroll-next-button"
                  >
                    Continue <ArrowRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</Label>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 text-slate-600">
    <span>{label}</span>
    <span className="font-medium text-slate-900">{value || "—"}</span>
  </div>
);
